import React, { useContext, useId, useState } from 'react';
import * as Sentry from '@sentry/browser';
import {
  ImageUpload,
  RadioButton,
  RadioGroup,
  useResponsiveValue,
} from '@monash/portal-react';
import DraggableWindow from '@/components/ui/draggable-window/DraggableWindow';
import c from './customise-clock.module.scss';
import { WidgetContext } from '@/components/providers/WidgetProvider';
import {
  useApi,
  postCustomImage,
  deleteCustomImage,
} from '@monash/portal-frontend-common';
import { MOBILE_RESPONSIVE } from '@/components/ui/main/Main';
import { COLOURS } from '../constants';
import ColourList from '@/components/pages/custom/widget-container/edit-menu/additional-options/colour-list/ColourList';
import { IMAGE_EFFECTS } from '../../../../../../constants';
import { useSnackbar } from '@/components/providers/SnackbarProvider';

const FIREBASE_COLOUR_KEY = 'colour';
const IMAGE_EFFECT_KEY = 'imageEffect';

export const CustomiseClock = ({
  data,
  updateData,
  escapeFocusRef,
  widgetId,
  setImageUrl,
  imageUrl,
  setImageEffect: setImageEffectLocal,
  imageEffect,
  isLoading,
  setIsLoading,
  isLegacyUrl,
}) => {
  const { STRETCH_TO_FIT, REPEAT, BLUR } = IMAGE_EFFECTS;
  const { editActiveWidget, setEditActiveWidget } = useContext(WidgetContext);
  const { addSnackbar } = useSnackbar();

  // File name returned by uploader
  const [fileName, setFileName] = useState(null);

  // File name inferred from imageUrl, mostly to support the lack
  // of file name from legacy backgroundURL
  const splitUrl = imageUrl?.split('/');
  const inferredFileName = splitUrl?.[splitUrl.length - 1];

  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
  const isMobile = responsiveSize === 'S';

  const { call: callPostCustomImage } = useApi({
    requestFn: postCustomImage,
    skipInitialCall: true,
  });

  const { call: callDeleteCustomImage } = useApi({
    requestFn: deleteCustomImage,
    skipInitialCall: true,
  });

  // deactivate widget from editing shortcuts in provider
  const deactivateWidget = () => {
    setEditActiveWidget(null);
  };

  // draggable window initial position
  const getTriggerRefPosition = () => {
    if (!escapeFocusRef?.current) return;
    const position = escapeFocusRef.current.getBoundingClientRect();
    return { x: position.right, y: position.bottom };
  };

  const HEADING_TEXT = 'Customise';

  const uploadImage = (fileName, encodedValue) => {
    setIsLoading(true);
    callPostCustomImage(fileName, widgetId, encodedValue)
      .then(() => {
        setIsLoading(false);
        setImageUrl(encodedValue);
        setFileName(fileName);
      })
      .catch((error) => {
        const SERVER_ERROR_MSG =
          "We're not able to save your image right now - please try again later";
        const CLIENT_ERROR_MSG =
          'This file cannot be accepted - please use a different file';
        const isUserError = String(error.status).startsWith('4');
        const message = isUserError ? CLIENT_ERROR_MSG : SERVER_ERROR_MSG;

        setIsLoading(false);
        addSnackbar({
          message,
          type: 'error',
        });
        Sentry.captureException(error);
      });
  };

  const deleteImage = () => {
    setIsLoading(true);

    if (isLegacyUrl) {
      updateData('backgroundURL', null);
      setImageUrl(null);
      setIsLoading(false);
    } else {
      callDeleteCustomImage(widgetId)
        .then(() => {
          setImageUrl(null);
          setFileName(null);
          setIsLoading(false);
          if (imageEffect !== STRETCH_TO_FIT) {
            setImageEffect(STRETCH_TO_FIT);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          Sentry.captureException(error);
          addSnackbar({
            message:
              "We're not able to remove your image right now - please try again later",
            type: 'error',
          });
        });
    }
  };

  const setImageEffect = (effect) => {
    setImageEffectLocal(effect);
    updateData(IMAGE_EFFECT_KEY, effect);
  };

  const imageEffectsHeading = useId();

  return (
    <div className={c.editClockBackground}>
      <DraggableWindow
        fullScreen={isMobile}
        title={HEADING_TEXT}
        isShowing={editActiveWidget}
        focusTrapEnabled={true}
        focusTrapSelectors={
          'button:not([disabled]),' +
          "[role='button']," + // for image input when it has no uploaded image
          'input[type=radio]:checked:not(:disabled)' // for image effect radio buttons
        }
        keyboardDismissalEnabled={true}
        onClose={() => {
          deactivateWidget();
          escapeFocusRef.current?.focus();
        }}
        initialPosition={getTriggerRefPosition()}
        extraRootProps={{
          role: 'dialog',
          'aria-label': HEADING_TEXT,
        }}
      >
        <div className={c.editClockBackground}>
          <div className={c.content}>
            <div className={c.item}>
              <label className={c.label}>Colours</label>

              <div className={c.colourListWrapper}>
                <ColourList
                  selectedColour={data?.colour}
                  onSelect={(colour) => updateData(FIREBASE_COLOUR_KEY, colour)}
                  colours={COLOURS}
                />
              </div>
            </div>

            <div className={c.item}>
              <ImageUpload
                onUpload={uploadImage}
                onDelete={deleteImage}
                fileName={data?.imageFileName || fileName || inferredFileName}
                imageURL={imageUrl}
                maxFileSize={7}
                isLoading={isLoading}
              />
            </div>

            <div className={c.item}>
              <div id={imageEffectsHeading} className={c.label}>
                Image effects
              </div>
              <RadioGroup aria-labelledby={imageEffectsHeading}>
                <RadioButton
                  onClick={() => setImageEffect(STRETCH_TO_FIT)}
                  on={imageEffect === STRETCH_TO_FIT}
                  label="Stretch to fit"
                  disabled={!imageUrl}
                />
                <RadioButton
                  onClick={() => setImageEffect(REPEAT)}
                  on={imageEffect === REPEAT}
                  label="Repeat"
                  disabled={!imageUrl}
                />
                <RadioButton
                  onClick={() => setImageEffect(BLUR)}
                  on={imageEffect === BLUR}
                  label="Blur"
                  disabled={!imageUrl}
                />
              </RadioGroup>
            </div>
          </div>
        </div>
      </DraggableWindow>
    </div>
  );
};
